import { useEffect } from "react";

import { useNorthbeamQuery } from "@hooks/http";
import {
  ConnectionType,
  type FetchSanitizedConnectionsByType,
  type FetchSanitizedConnectionsByTypeVariables,
} from "@north-beam/nb-client-side";
import { type ConnectionValidationResult } from "@north-beam/nb-common";
import { AmazonConnectionCard } from "@pages/settings/connections/amazon";
import { AmazonSellerConnectionCard } from "@pages/settings/connections/amazon-seller";
import { ApplovinConnectionCard } from "@pages/settings/connections/applovin";
import { AttentiveMobileConnectionCard } from "@pages/settings/connections/attentive-mobile";
import { FBConnectionCard } from "@pages/settings/connections/facebook";
import { AdwordsConnectionCard } from "@pages/settings/connections/google-ads";
import { KlaviyoConnectionCard } from "@pages/settings/connections/klaviyo";
import { MicrosoftConnectionCard } from "@pages/settings/connections/microsoft";
import { MNTNConnectionCard } from "@pages/settings/connections/mntn";
import { PinterestConnectionCard } from "@pages/settings/connections/pinterest";
import { RakutenConnectionCard } from "@pages/settings/connections/rakuten";
import { SnapchatConnectionCard } from "@pages/settings/connections/snapchat";
import { TatariConnectionCard } from "@pages/settings/connections/tatari";
import { TikTokConnectionCard } from "@pages/settings/connections/tiktok";
import { TradeDeskConnectionCard } from "@pages/settings/connections/trade-desk";
import { TwitterConnectionCard } from "@pages/settings/connections/twitter";
import { FETCH_SANITIZED_CONNECTIONS_BY_TYPE } from "@pages/settings/queries";

type ConnectionCard = React.FC<{ integrationDetails: any }>;

const _connectionCards: {
  [key in ConnectionType]?: ConnectionCard;
} = {
  [ConnectionType.integration_amazon]: AmazonConnectionCard,
  [ConnectionType.integration_amazon_seller]: AmazonSellerConnectionCard,
  [ConnectionType.integration_attentive]: AttentiveMobileConnectionCard,
  [ConnectionType.integration_facebook_ads]: FBConnectionCard,
  [ConnectionType.integration_google_adwords]: AdwordsConnectionCard,
  [ConnectionType.integration_klaviyo]: KlaviyoConnectionCard,
  [ConnectionType.integration_microsoft]: MicrosoftConnectionCard,
  [ConnectionType.integration_mntn]: MNTNConnectionCard,
  [ConnectionType.integration_pinterest]: PinterestConnectionCard,
  [ConnectionType.integration_rakuten]: RakutenConnectionCard,
  [ConnectionType.integration_snapchat]: SnapchatConnectionCard,
  [ConnectionType.integration_tatari]: TatariConnectionCard,
  [ConnectionType.integration_tiktok]: TikTokConnectionCard,
  [ConnectionType.integration_trade_desk]: TradeDeskConnectionCard,
  [ConnectionType.integration_twitter]: TwitterConnectionCard,
  [ConnectionType.integration_applovin]: ApplovinConnectionCard,
};

export const PlatformConnectionSetup = ({
  connectionType = "manual",
  integrationDetails,
  toggleIsConfigured,
}: {
  connectionType?: ConnectionType | "manual";
  integrationDetails: any;
  toggleIsConfigured: (isConfigured: boolean) => void;
}) => {
  // TODO add refetch and poll for status
  const { data, loading } = useNorthbeamQuery<
    FetchSanitizedConnectionsByType,
    FetchSanitizedConnectionsByTypeVariables
  >(FETCH_SANITIZED_CONNECTIONS_BY_TYPE, {
    variables: {
      type:
        !connectionType || connectionType === "manual" ? null : connectionType,
    },
    skip: !connectionType || connectionType === "manual",
  });

  useEffect(() => {
    if (loading || !data) {
      return;
    }
    const connections = data?.me.sanitizedConnections;
    if (!connections || !connections.length) {
      toggleIsConfigured(false);
      return;
    }
    const validations = connections.map(
      (conn) => conn.connectionValidation as ConnectionValidationResult,
    );
    if (
      validations.some((validation) =>
        validation.connectionLevelIssues.some((v) => v.reconnectRecommended),
      ) ||
      validations.some((validation) =>
        validation.connectedAccounts.some(
          (v) => v.connectionIssue?.reconnectRecommended,
        ),
      )
    ) {
      toggleIsConfigured(false);
      return;
    }

    toggleIsConfigured(true);
  }, [toggleIsConfigured, data, loading]);

  if (connectionType === "manual") {
    return null;
  }

  const connectionCard = _connectionCards[connectionType];

  return (
    <>
      {connectionCard?.({
        integrationDetails,
      })}
    </>
  );
};
